import { nanoid } from 'nanoid';
import { anglesFromEndpoints } from './distanceCalculator.js';

const LS_KEY = 'mdv-saved-measurements';

const MEASURE_COLORS = [
  '#38bdf8',
  '#a78bfa',
  '#fb7185',
  '#34d399',
  '#fb923c',
  '#22d3ee',
  '#f472b6',
  '#4ade80',
  '#818cf8',
  '#facc15',
  '#2dd4bf',
  '#f87171',
  '#c084fc',
  '#67e8f9',
  '#eab308',
];

function isHexColor(value) {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);
}

export function measureColor(m) {
  return isHexColor(m?.color) ? m.color : '#eab308';
}

function nextMeasureColor(used) {
  const taken = new Set((used || []).filter(isHexColor).map((c) => c.toLowerCase()));
  const free = MEASURE_COLORS.filter((c) => !taken.has(c.toLowerCase()));
  const pool = free.length ? free : MEASURE_COLORS;
  return pool[Math.floor(Math.random() * pool.length)];
}

function ensureMeasureMeta(list) {
  const next = (list || []).map((m) => ({ ...m }));
  let max = 0;
  next.forEach((m) => {
    const n = Number(m.n);
    if (Number.isInteger(n) && n > 0) max = Math.max(max, n);
  });
  next
    .filter((m) => !Number.isInteger(Number(m.n)) || Number(m.n) <= 0)
    .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')))
    .forEach((m) => {
      max += 1;
      m.n = max;
    });
  const used = next.map((m) => m.color);
  next.forEach((m, i) => {
    if (isHexColor(m.color)) return;
    m.color = nextMeasureColor(used);
    used[i] = m.color;
  });
  next.forEach((m) => {
    const angles = anglesFromEndpoints(m.p1, m.p2);
    if (!angles) return;
    m.a = angles.a;
    m.d = angles.d;
    m.dist = angles.dist;
    m.dy = angles.dy;
  });
  return next;
}

export function measureTag(m) {
  const n = Number(m?.n);
  if (Number.isInteger(n) && n > 0) return `M${n}`;
  return 'M?';
}

export function measureInfoText(m) {
  if (!m) return '';
  return `${measureTag(m)}  Direction:${m.a}(deg) Dip:${m.d}(deg) Elevation:${m.dy}m Distance:${m.dist}m`;
}

export function readSavedMeasurements() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    const ensured = ensureMeasureMeta(list);
    const needsWrite = list.some((m, i) => {
      const e = ensured[i];
      if (!e) return true;
      return (
        m.n !== e.n ||
        m.color !== e.color ||
        String(m.a) !== String(e.a) ||
        String(m.d) !== String(e.d) ||
        String(m.dist) !== String(e.dist)
      );
    });
    if (needsWrite && ensured.length) {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(ensured));
      } catch {
        /* ignore quota */
      }
    }
    return ensured;
  } catch {
    return [];
  }
}

function writeList(list) {
  const next = ensureMeasureMeta(Array.isArray(list) ? list : []);
  localStorage.setItem(LS_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent('mdv-measurements-changed'));
  return next;
}

export function addSavedMeasurement(entry) {
  const existing = readSavedMeasurements();
  const n = 1 + existing.reduce((mx, m) => Math.max(mx, Number(m.n) || 0), 0);
  const item = {
    id: nanoid(8),
    createdAt: new Date().toISOString(),
    ...entry,
    n: Number(entry?.n) > 0 ? Number(entry.n) : n,
    color: isHexColor(entry?.color) ? entry.color : nextMeasureColor(existing.map((m) => m.color)),
  };
  return writeList([item, ...existing].slice(0, 80));
}

export function deleteSavedMeasurement(id) {
  return writeList(readSavedMeasurements().filter((m) => m.id !== id));
}

export function clearSavedMeasurements() {
  return writeList([]);
}

export function measurementsToCsv(list, origin) {
  const east = Number(origin?.east) || 0;
  const north = Number(origin?.north) || 0;
  const rl = Number(origin?.rl) || 0;
  const header = [
    'id',
    'label',
    'color',
    'distance_m',
    'dip_deg',
    'direction_deg',
    'elevation_m',
    'x1',
    'y1',
    'z1',
    'x2',
    'y2',
    'z2',
    'createdAt',
  ];
  const rows = (list || []).map((m) => {
    const p1 = m.p1 || {};
    const p2 = m.p2 || {};
    const x1 = (p1.x ?? 0) + east;
    const y1 = north - (p1.z ?? 0);
    const z1 = (p1.y ?? 0) + rl;
    const x2 = (p2.x ?? 0) + east;
    const y2 = north - (p2.z ?? 0);
    const z2 = (p2.y ?? 0) + rl;
    return [
      m.id,
      measureTag(m),
      measureColor(m),
      m.dist,
      m.d,
      m.a,
      m.dy,
      x1.toFixed(2),
      y1.toFixed(2),
      z1.toFixed(2),
      x2.toFixed(2),
      y2.toFixed(2),
      z2.toFixed(2),
      m.createdAt || '',
    ].join(',');
  });
  return [header.join(','), ...rows].join('\n');
}

export function downloadMeasurementsCsv(list, origin, filename) {
  const csv = measurementsToCsv(list, origin);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `measurements-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}
